import { useState, useEffect } from 'react';
import { FiX, FiSave, FiSearch, FiTag } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Modal para agregar relaciones entre pasajes bíblicos
 * Incluye catálogos completos y buscador de relaciones Padre-Hijo
 */
const BibleRelationModal = ({
  isOpen,
  onClose,
  onSave,
  sourceReference = '',
  book = '',
  chapter = 0,
  verses = [],
  initialRelation = null,
  user: propUser,
  modulePermissions = []
}) => {
  // Estados básicos
  const [title, setTitle] = useState(initialRelation?.title || '');
  const [content, setContent] = useState(initialRelation?.content || '');
  const [targetReference, setTargetReference] = useState(initialRelation?.targetReference || '');
  const [keywords, setKeywords] = useState(initialRelation?.keywords || '');
  const [saving, setSaving] = useState(false);

  // Estados para catálogos
  const [relationTypes, setRelationTypes] = useState([]);
  const [applicationGroups, setApplicationGroups] = useState([]);
  const [applicationPlaces, setApplicationPlaces] = useState([]);
  const [applicationTimes, setApplicationTimes] = useState([]);
  const [punishmentTypes, setPunishmentTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Estados seleccionados
  const [selectedRelationType, setSelectedRelationType] = useState(initialRelation?.relationTypeId || null);
  const [selectedGroup, setSelectedGroup] = useState(initialRelation?.applicationGroupId || null);
  const [selectedPlace, setSelectedPlace] = useState(initialRelation?.applicationPlaceId || null);
  const [selectedTime, setSelectedTime] = useState(initialRelation?.applicationTimeId || null);
  const [selectedPunishment, setSelectedPunishment] = useState(initialRelation?.punishmentTypeId || null);
  const [selectedCategories, setSelectedCategories] = useState(initialRelation?.categories || []);
  
  // Estados para relación Padre-Hijo
  const [isParent, setIsParent] = useState(initialRelation?.isParent !== false); // Por defecto es Padre
  const [parentRelationId, setParentRelationId] = useState(initialRelation?.parentRelationId || null);
  const [showParentSearch, setShowParentSearch] = useState(false);
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [parentSearchResults, setParentSearchResults] = useState([]);
  const [selectedParentRelation, setSelectedParentRelation] = useState(null);

  const { user: contextUser } = useAuth();
  const user = propUser || contextUser;

  // Actualizar estados cuando cambia initialRelation
  useEffect(() => {
    if (initialRelation) {
      setTitle(initialRelation.title || '');
      setContent(initialRelation.content || '');
      setTargetReference(initialRelation.targetReference || initialRelation.verseTo || '');
      setKeywords(typeof initialRelation.keywords === 'string' ? initialRelation.keywords : '');
      setSelectedRelationType(initialRelation.relationTypeId || null);
      setSelectedGroup(initialRelation.applicationGroupId || null);
      setSelectedPlace(initialRelation.applicationPlaceId || null);
      setSelectedTime(initialRelation.applicationTimeId || null);
      setSelectedPunishment(initialRelation.punishmentTypeId || null);
      setIsParent(initialRelation.isParent !== false);
      setParentRelationId(initialRelation.parentRelationId || null);
      
      // Manejar categoryIds
      const catIds = initialRelation.categories?.map(c => c.id || c) || initialRelation.categoryIds || [];
      setSelectedCategories(catIds);
      
      // Si tiene parentRelationId, buscar y seleccionar
      if (initialRelation.parentRelationId) {
        setSelectedParentRelation({
          id: initialRelation.parentRelationId,
          title: initialRelation.parentTitle || 'Relación seleccionada'
        });
      } else {
        setSelectedParentRelation(null);
      }
    } else {
      // Limpiar para nueva relación
      setTitle('');
      setContent('');
      setTargetReference('');
      setKeywords('');
      setSelectedRelationType(null);
      setSelectedGroup(null);
      setSelectedPlace(null);
      setSelectedTime(null);
      setSelectedPunishment(null);
      setSelectedCategories([]);
      setIsParent(true);
      setParentRelationId(null);
      setSelectedParentRelation(null);
    }
  }, [initialRelation]);

  // Cargar catálogos
  useEffect(() => {
    if (isOpen) {
      loadCatalogs();
    }
  }, [isOpen]);

  const loadCatalogs = async () => {
    try {
      // Cargar desde la base de datos local
      const { default: api } = await import('../../utils/api');
      
      // Cargar tipos de relaciones desde el backend
      const relTypesRes = await api.get('/bible/relation-types');
      if (relTypesRes.data.success) {
        setRelationTypes(relTypesRes.data.data);
      }
      
      // Cargar grupos de aplicación
      const groupsRes = await api.get('/bible/application-groups');
      if (groupsRes.data.success) {
        setApplicationGroups(groupsRes.data.data);
      }
      
      // Cargar lugares de aplicación
      const placesRes = await api.get('/bible/application-places');
      if (placesRes.data.success) {
        setApplicationPlaces(placesRes.data.data);
      }
      
      // Cargar tiempos de aplicación
      const timesRes = await api.get('/bible/application-times');
      if (timesRes.data.success) {
        setApplicationTimes(timesRes.data.data);
      }
      
      // Cargar tipos de castigo
      const punishmentsRes = await api.get('/bible/punishment-types');
      if (punishmentsRes.data.success) {
        setPunishmentTypes(punishmentsRes.data.data);
      }
      
      // Cargar categorías
      const categoriesRes = await api.get('/categorias');
      if (categoriesRes.data.success) {
        setCategories(categoriesRes.data.data);
      }
    } catch (error) {
      console.error('Error cargando catálogos:', error);
      // Si falla el backend, intentar cargar desde localStorage
      loadCatalogsFromLocalStorage();
    }
  };

  const loadCatalogsFromLocalStorage = async () => {
    try {
      const { default: storageService } = await import('../../services/storageService');
      // Aquí podrías implementar carga desde IndexedDB si los catálogos se guardan localmente
      console.log('Intentando cargar catálogos desde almacenamiento local...');
    } catch (error) {
      console.error('Error cargando catálogos desde local:', error);
    }
  };

  // Buscar relaciones Padre/Hijo
  const searchParentRelations = async (query) => {
    if (!query || query.length < 2) {
      setParentSearchResults([]);
      return;
    }

    if (!user) return;

    try {
      const { BibleStorageService } = await import('../../utils/bibleStorageHelper');
      const bibleStorage = new BibleStorageService(user, modulePermissions);

      // Buscar relaciones
      const results = await bibleStorage.searchParentRelations(query);

      setParentSearchResults(results)
    } catch (error) {
      console.error('Error buscando relaciones:', error);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (showParentSearch) {
        searchParentRelations(parentSearchQuery);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [parentSearchQuery, showParentSearch]);

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const selectParentRelation = (relation) => {
    setSelectedParentRelation(relation);
    setParentRelationId(relation.id);
    setShowParentSearch(false);
    setParentSearchQuery('');
  };

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!content.trim()) {
      alert('Por favor escribe el contenido de la relación');
      return;
    }

    if (!selectedRelationType) {
      alert('Por favor selecciona un tipo de relación');
      return;
    }

    if (!user) {
      alert('Debes iniciar sesión');
      return;
    }

    setSaving(true);
    try {
      const { BibleStorageService } = await import('../../utils/bibleStorageHelper');
      const bibleStorage = new BibleStorageService(user, modulePermissions);

      const relationData = {
        title: title || 'Sin título',
        content: content,
        relationTypeId: selectedRelationType || null,
        book: book,
        chapter: chapter,
        verseStart: Math.min(...verses),
        verseEnd: Math.max(...verses),
        verseFrom: sourceReference || null,
        verseTo: targetReference || null,
        keywords: keywords || null,
        applicationGroupId: selectedGroup || null,
        applicationPlaceId: selectedPlace || null,
        applicationTimeId: selectedTime || null,
        punishmentTypeId: selectedPunishment || null,
        isParent: isParent,
        isPublic: false,
        categoryIds: selectedCategories.map(cat => cat.value).filter(id => id != null) // Extraer solo los IDs válidos
      };

      let savedRelationId;
      if (initialRelation?.id) {
        await bibleStorage.updateRelation(initialRelation.id, relationData);
        savedRelationId = initialRelation.id;
      } else {
        const result = await bibleStorage.addRelation(relationData);
        savedRelationId = result;
      }

      // Si es una relación Hijo y se seleccionó un Padre, crear la jerarquía
      if (!isParent && parentRelationId && savedRelationId) {
        await bibleStorage.addRelationHierarchy({
          parentRelationId: parentRelationId,
          childRelationId: savedRelationId,
          createdAt: new Date().toISOString()
        });
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error guardando relación:', error);
      console.error('Error completo:', error.response?.data);
      alert(`Error al guardar la relación: ${error.response?.data?.message || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Reset states
    setTitle('');
    setContent('');
    setTargetReference('');
    setKeywords('');
    setSelectedRelationType(null);
    setSelectedGroup(null);
    setSelectedPlace(null);
    setSelectedTime(null);
    setSelectedPunishment(null);
    setSelectedCategories([]);
    setIsParent(true);
    setParentRelationId(null);
    setSelectedParentRelation(null);
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {initialRelation ? 'Editar Relación' : 'Nueva Relación Bíblica'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Desde: <span className="font-medium text-gray-700">{sourceReference}</span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título de la Relación
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Mandamiento de amar al prójimo"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Tipo de Relación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Relación <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRelationType || ''}
              onChange={(e) => setSelectedRelationType(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Selecciona un tipo...</option>
              {relationTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name} - {type.description}
                </option>
              ))}
            </select>
          </div>

          {/* Referencia Relacionada */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Referencia Relacionada (opcional)
            </label>
            <input
              type="text"
              value={targetReference}
              onChange={(e) => setTargetReference(e.target.value)}
              placeholder="Ej: Juan 3:16, Génesis 1:1-3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Contenido */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción de la Relación <span className="text-red-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Describe la relación entre estos pasajes..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Palabras Clave */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiTag className="inline w-4 h-4 mr-1" />
              Palabras Clave (separadas por comas)
            </label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="Ej: mandamiento, amor, prójimo"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Tipo de Relación: Padre o Hijo */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Jerarquía de Relación
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={isParent}
                  onChange={() => {
                    setIsParent(true);
                    setParentRelationId(null);
                    setSelectedParentRelation(null);
                  }}
                  className="w-4 h-4 text-purple-600"
                />
                <span className="text-sm">
                  <strong>Relación Padre</strong> (independiente)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!isParent}
                  onChange={() => setIsParent(false)}
                  className="w-4 h-4 text-purple-600"
                />
                <span className="text-sm">
                  <strong>Relación Hijo</strong> (depende de otra)
                </span>
              </label>
            </div>
          </div>

          {/* Buscador de Relación Padre (solo si es Hijo) */}
          {!isParent && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-purple-900 mb-2">
                <FiSearch className="inline w-4 h-4 mr-1" />
                Buscar Relación Padre o Hermano
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={parentSearchQuery}
                  onChange={(e) => {
                    setParentSearchQuery(e.target.value);
                    setShowParentSearch(true);
                  }}
                  onFocus={() => setShowParentSearch(true)}
                  placeholder="Busca por título o palabras clave..."
                  className="w-full px-4 py-2 pl-10 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-400" />
              </div>

              {/* Relación Padre Seleccionada */}
              {selectedParentRelation && (
                <div className="mt-3 p-3 bg-white border border-purple-300 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedParentRelation.title}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {selectedParentRelation.verseFrom}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedParentRelation(null);
                        setParentRelationId(null);
                      }}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Quitar
                    </button>
                  </div>
                </div>
              )}

              {/* Resultados de Búsqueda */}
              {showParentSearch && parentSearchResults.length > 0 && !selectedParentRelation && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-purple-200 rounded-lg bg-white">
                  {parentSearchResults.map(relation => (
                    <button
                      key={relation.id}
                      onClick={() => selectParentRelation(relation)}
                      className="w-full text-left p-3 hover:bg-purple-50 border-b border-purple-100 last:border-b-0 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900">
                        {relation.title}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {relation.verseFrom}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {relation.content}
                      </p>
                    </button>
                  ))}
                </div>
              )}

              {showParentSearch && parentSearchQuery && parentSearchResults.length === 0 && (
                <p className="mt-2 text-xs text-purple-700">
                  No se encontraron relaciones. Escribe al menos 2 caracteres para buscar.
                </p>
              )}
            </div>
          )}

          {/* Catálogos de Aplicación */}
          <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Grupo de Aplicación */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grupo de Aplicación
              </label>
              <select
                value={selectedGroup || ''}
                onChange={(e) => setSelectedGroup(parseInt(e.target.value) || null)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Ninguno</option>
                {applicationGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Lugar de Aplicación */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lugar de Aplicación
              </label>
              <select
                value={selectedPlace || ''}
                onChange={(e) => setSelectedPlace(parseInt(e.target.value) || null)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Ninguno</option>
                {applicationPlaces.map(place => (
                  <option key={place.id} value={place.id}>
                    {place.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tiempo de Aplicación */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tiempo de Aplicación
              </label>
              <select
                value={selectedTime || ''}
                onChange={(e) => setSelectedTime(parseInt(e.target.value) || null)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Ninguno</option>
                {applicationTimes.map(time => (
                  <option key={time.id} value={time.id}>
                    {time.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tipo de Castigo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Castigo
              </label>
              <select
                value={selectedPunishment || ''}
                onChange={(e) => setSelectedPunishment(parseInt(e.target.value) || null)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Ninguno</option>
                {punishmentTypes.map(punishment => (
                  <option key={punishment.id} value={punishment.id}>
                    {punishment.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Categorías */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categorías (puedes seleccionar varias)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {categories.map(cat => (
                <label
                  key={cat.id}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(cat.id)}
                    onChange={() => toggleCategory(cat.id)}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{cat.nombre}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <span className="inline-block w-2 h-2 bg-purple-500 rounded-full"></span>
            Guardado localmente
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FiSave className="w-5 h-5" />
              {saving ? 'Guardando...' : 'Guardar Relación'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BibleRelationModal;
